import { z } from 'zod';
import { AccountStatus, CompanyRole, Id, InstanceId, Timestamp, singleLine } from './common';
import { endpoint } from './endpoint';
import { MailStatus, PersonRef } from './invitations';
import { lenient } from './lenient';
import { LIMITS } from './limits';
import { EditWindow, MailConnection, ScannerHealth } from './server-info';
import { Person } from './users';

/** Company administration. Admin views never include conversation content. */
export const AdminMember = z.object({
  person: Person,
  role: lenient(CompanyRole, 'member'),
  status: lenient(AccountStatus, 'deactivated'),
  mfaEnabled: z.boolean(),
  lastSeenAt: Timestamp.nullable(),
  createdAt: Timestamp,
  deactivatedAt: Timestamp.nullable(),
});
export type AdminMember = z.infer<typeof AdminMember>;

export const CompanySettings = z.object({
  companyName: z.string(),
  /** Off: members' invitations wait for admin approval (default). */
  allowMemberDirectInvite: z.boolean(),
  inviteTtlDays: z.number().int().positive(),
  editWindow: lenient(EditWindow, 'none'),
  /** The server fetches previews of links in messages (on unless the owner turns it off). Absent on older servers. */
  linkPreviews: z.boolean().optional(),
  /** Per file, at most LIMITS.maxFileBytes. Absent on older servers (they allow LIMITS.maxFileBytes). */
  maxFileBytes: z.number().int().positive().optional(),
  /** Programs and installers may be sent; receivers see a warning. Absent on older servers (they refuse them). */
  allowExecutables: z.boolean().optional(),
  /** The company's storage limit, null for none (the VM's own limit still applies). Absent on older servers. */
  storageQuotaBytes: z.number().int().positive().nullable().optional(),
});
export type CompanySettings = z.infer<typeof CompanySettings>;

const RetentionMode = z.enum(['none', 'days']);

export const RetentionPolicy = z.object({
  /** `none` = active messages/files never expire (default). */
  mode: lenient(RetentionMode, 'none'),
  days: z.number().int().min(30).max(3650).nullable(),
  updatedAt: Timestamp.nullable(),
  updatedBy: Id.nullable(),
  /** When a shortened policy starts deleting (preview shown before applying). */
  effectiveFrom: Timestamp.nullable(),
  /** While a shorter policy is announced: the policy that still applies until `effectiveFrom`. */
  inForce: z.object({ mode: lenient(RetentionMode, 'none'), days: z.number().int().min(30).max(3650).nullable() }).optional(),
});
export type RetentionPolicy = z.infer<typeof RetentionPolicy>;

const RetentionInput = z
  .object({ mode: RetentionMode, days: z.number().int().min(30).max(3650).optional() })
  .refine((v) => (v.mode === 'days') === (v.days !== undefined), 'days is required exactly when mode is "days"');

/** A group left with no owner or admin, e.g. after its owner was deactivated. Names only, never content. */
export const OwnerlessGroup = z.object({
  conversationId: Id,
  title: z.string(),
  /** Active members; one of them becomes the owner. */
  members: z.array(PersonRef),
});
export type OwnerlessGroup = z.infer<typeof OwnerlessGroup>;

const memberParam = z.object({ userId: Id });

export const adminEndpoints = {
  members: endpoint({
    id: 'admin.members',
    method: 'GET',
    path: '/api/v1/admin/members',
    auth: 'admin',
    response: z.object({ members: z.array(AdminMember) }),
  }),
  /**
   * Appointing admins is an owner duty; ownership moves only via transferOwnership.
   * A new admin's sessions that did not pass two-step verification are signed out.
   */
  setRole: endpoint({
    id: 'admin.setRole',
    method: 'PATCH',
    path: '/api/v1/admin/members/:userId',
    auth: 'owner',
    params: memberParam,
    body: z.object({ role: z.enum(['admin', 'member']) }),
    response: z.object({ member: AdminMember }),
  }),
  /**
   * Revokes every session and socket; messages and files stay per retention policy;
   * groups owned by the person pass to an existing group admin.
   */
  deactivate: endpoint({
    id: 'admin.deactivate',
    method: 'POST',
    path: '/api/v1/admin/members/:userId/deactivate',
    auth: 'admin',
    params: memberParam,
    response: z.object({
      member: AdminMember,
      revokedSessions: z.number().int().nonnegative(),
      /** Groups with no admin to inherit ownership; a company admin picks the successor (`assignGroupOwner`). */
      groupsNeedingOwner: z.array(z.object({ conversationId: Id, title: z.string() })),
    }),
  }),
  ownerlessGroups: endpoint({
    id: 'admin.ownerlessGroups',
    method: 'GET',
    path: '/api/v1/admin/groups/ownerless',
    auth: 'admin',
    response: z.object({ groups: z.array(OwnerlessGroup) }),
  }),
  /**
   * Makes an active member the owner of a group without owner or admin and posts an
   * owner.changed line. The company admin does not join the group. Returns the groups still ownerless.
   */
  assignGroupOwner: endpoint({
    id: 'admin.assignGroupOwner',
    method: 'POST',
    path: '/api/v1/admin/groups/:conversationId/owner',
    auth: 'admin',
    params: z.object({ conversationId: Id }),
    body: z.object({ userId: Id }),
    response: z.object({ groups: z.array(OwnerlessGroup) }),
  }),
  /**
   * Moderation: removes someone's profile photo and records it in the audit log.
   * Only the owner removes an admin's photo; the owner's own photo is theirs alone.
   */
  removeAvatar: endpoint({
    id: 'admin.removeAvatar',
    method: 'DELETE',
    path: '/api/v1/admin/members/:userId/avatar',
    auth: 'admin',
    params: memberParam,
    response: z.object({ member: AdminMember }),
  }),
  /**
   * Only on a server that sends no mail (ADR-0007): a one-time password reset link the admin hands
   * over to the person, valid for a day. Earlier links stop working; using it signs out every device.
   * Admins issue them for members, the owner also for admins; owners recover from the VM console.
   */
  passwordResetLink: endpoint({
    id: 'admin.passwordResetLink',
    method: 'POST',
    path: '/api/v1/admin/members/:userId/password-reset',
    auth: 'admin',
    params: memberParam,
    response: z.object({ link: z.url(), expiresAt: Timestamp }),
    rateLimit: 'invite',
  }),
  /** Does not restore previous sessions or group memberships. */
  reactivate: endpoint({
    id: 'admin.reactivate',
    method: 'POST',
    path: '/api/v1/admin/members/:userId/reactivate',
    auth: 'admin',
    params: memberParam,
    response: z.object({ member: AdminMember }),
  }),
  /** The previous owner stays an admin; the new owner's sessions without two-step verification are signed out. */
  transferOwnership: endpoint({
    id: 'admin.transferOwnership',
    method: 'POST',
    path: '/api/v1/admin/ownership/transfer',
    auth: 'owner',
    body: z.object({ toUserId: Id }),
    response: z.object({ from: AdminMember, to: AdminMember }),
  }),
  settings: endpoint({ id: 'admin.settings', method: 'GET', path: '/api/v1/admin/settings', auth: 'admin', response: CompanySettings }),
  /** Company settings belong to the owner; admins can read them. */
  updateSettings: endpoint({
    id: 'admin.updateSettings',
    method: 'PATCH',
    path: '/api/v1/admin/settings',
    auth: 'owner',
    body: z.object({
      companyName: singleLine({ min: 1, max: 60 }).optional(),
      allowMemberDirectInvite: z.boolean().optional(),
      editWindow: EditWindow.optional(),
      linkPreviews: z.boolean().optional(),
      maxFileBytes: z
        .number()
        .int()
        .min(1024 * 1024)
        .max(LIMITS.maxFileBytes)
        .optional(),
      allowExecutables: z.boolean().optional(),
      storageQuotaBytes: z
        .number()
        .int()
        .min(1024 ** 3)
        .nullable()
        .optional(),
    }),
    response: CompanySettings,
  }),
  retention: endpoint({
    id: 'admin.retention',
    method: 'GET',
    path: '/api/v1/admin/retention',
    auth: 'admin',
    response: z.object({ policy: RetentionPolicy, deletedContentGraceDays: z.number().int().positive() }),
  }),
  /** Counts what a policy would delete and when, before it is applied. */
  previewRetention: endpoint({
    id: 'admin.previewRetention',
    method: 'POST',
    path: '/api/v1/admin/retention/preview',
    auth: 'owner',
    body: RetentionInput,
    response: z.object({
      messages: z.number().int().nonnegative(),
      files: z.number().int().nonnegative(),
      bytes: z.number().int().nonnegative(),
      effectiveFrom: Timestamp.nullable(),
    }),
  }),
  setRetention: endpoint({
    id: 'admin.setRetention',
    method: 'PUT',
    path: '/api/v1/admin/retention',
    auth: 'owner',
    body: RetentionInput,
    response: z.object({ policy: RetentionPolicy }),
  }),
  /** Sends a test message to the caller through the server's mail provider; CONFLICT when it sends no mail. */
  testMail: endpoint({
    id: 'admin.testMail',
    method: 'POST',
    path: '/api/v1/admin/mail/test',
    auth: 'admin',
    response: z.object({ mailId: Id, status: lenient(MailStatus, 'QUEUED') }),
    successStatus: 202,
    rateLimit: 'invite',
  }),
  server: endpoint({
    id: 'admin.server',
    method: 'GET',
    path: '/api/v1/admin/server',
    auth: 'admin',
    response: z.object({
      instanceId: InstanceId,
      companyName: z.string(),
      serverUrl: z.string(),
      serverVersion: z.string(),
      supportedClients: z.object({ min: z.string(), max: z.string().nullable() }),
      mail: z.object({
        status: lenient(MailConnection, 'degraded'),
        provider: z.string(),
        /** Why mail is degraded: held until the provider accepts this VM again, or failing (sends in a row not accepted). Absent while it works, and from older servers. */
        problem: lenient(z.enum(['held', 'failing']).optional(), undefined),
        /** The worker's short code for the last send that failed (`connection_failed`, `smtp_554_5_7_1`), never the provider's reply. */
        lastError: lenient(z.string().max(64).optional(), undefined),
      }),
      scanner: z.object({ mode: z.string(), status: lenient(ScannerHealth, 'degraded') }),
      storage: z.object({
        usedBytes: z.number().int().nonnegative(),
        reservedBytes: z.number().int().nonnegative(),
        freeBytes: z.number().int().nonnegative().nullable(),
        /** The limit in force: the lower of the company's and the VM's, null for none. Absent from older servers. */
        quotaBytes: z.number().int().positive().nullable().optional(),
      }),
      /** Error log lines of the server and the worker in the last 24 hours, kept across restarts and upgrades. Absent from older servers. */
      errors: z.object({ last24h: z.number().int().nonnegative(), lastAt: z.string().nullable() }).optional(),
    }),
  }),
};
